import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Instagram, Send, Check } from "lucide-react";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || "https://lirkprint-backend.onrender.com").replace(/\/$/, "");
const API = `${BASE_URL}/api`;

export default function Contact() {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", subject: "general", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const subjects = [
    { value: "general", label: t("contact.subjects.general") },
    { value: "order", label: t("contact.subjects.order") },
    { value: "custom", label: t("contact.subjects.custom") },
    { value: "collab", label: t("contact.subjects.collab") },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        subject: subjects.find(s => s.value === form.subject)?.label || form.subject,
        message: form.message
      });
      setSuccess(true);
      setForm({ name: "", email: "", subject: "general", message: "" });
    } catch {
      setError(t("contact.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pt-28">
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <span className="text-[11px] font-mono tracking-[0.3em] uppercase text-foreground/30 mb-3 block">
            {t("contact.subtitle")}
          </span>
          <h1 className="text-5xl font-heading font-bold tracking-tighter text-foreground">{t("contact.title")}</h1>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Form */}
          <div className="lg:col-span-2">
            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="contact-success"
                className="flex flex-col items-center justify-center py-20 text-center"
              >
                <div className="w-16 h-16 bg-foreground rounded-full flex items-center justify-center mb-6">
                  <Check size={24} strokeWidth={1.5} className="text-background" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground mb-3">{t("contact.success")}</h2>
                <p className="text-foreground/50">{lang === "de" ? "Wir melden uns schnellstmöglich." : "We'll get back to you as soon as possible."}</p>
                <button
                  onClick={() => setSuccess(false)}
                  className="mt-8 px-6 py-3 border border-border text-foreground/60 text-xs font-mono tracking-widest uppercase rounded-sm hover:border-foreground/40 hover:text-foreground transition-all"
                >
                  {lang === "de" ? "Weitere Nachricht senden" : "Send another message"}
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-mono tracking-wider uppercase text-foreground/40 mb-2">{t("contact.name")}</label>
                    <input
                      data-testid="contact-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full px-4 py-3 bg-surface border border-border rounded-sm text-foreground text-sm focus:outline-none focus:border-foreground/40 transition-colors placeholder:text-foreground/20"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono tracking-wider uppercase text-foreground/40 mb-2">{t("contact.email")}</label>
                    <input
                      data-testid="contact-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full px-4 py-3 bg-surface border border-border rounded-sm text-foreground text-sm focus:outline-none focus:border-foreground/40 transition-colors placeholder:text-foreground/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono tracking-wider uppercase text-foreground/40 mb-2">{t("contact.subject")}</label>
                  <select
                    data-testid="contact-subject"
                    value={form.subject}
                    onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                    className="w-full px-4 py-3 bg-surface border border-border rounded-sm text-foreground text-sm focus:outline-none focus:border-foreground/40 transition-colors"
                  >
                    {subjects.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-mono tracking-wider uppercase text-foreground/40 mb-2">{t("contact.message")}</label>
                  <textarea
                    data-testid="contact-message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    className="w-full px-4 py-3 bg-surface border border-border rounded-sm text-foreground text-sm focus:outline-none focus:border-foreground/40 transition-colors placeholder:text-foreground/20 resize-none"
                  />
                </div>

                {error && (
                  <p data-testid="contact-error" className="text-xs text-destructive font-mono">{error}</p>
                )}

                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-3 px-8 py-4 bg-foreground text-background text-xs font-mono tracking-[0.2em] uppercase rounded-sm hover:bg-foreground/90 transition-all disabled:opacity-40"
                >
                  <Send size={14} strokeWidth={1.5} />
                  {loading ? (lang === "de" ? "Senden..." : "Sending...") : t("contact.send")}
                </button>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <p className="text-[10px] font-mono tracking-[0.3em] uppercase text-foreground/30 mb-6">
                {lang === "de" ? "Direkter Kontakt" : "Direct Contact"}
              </p>
              <div className="space-y-4">
                <a href="mailto:hello@lirkprint.com" className="flex items-center gap-3 text-foreground/60 hover:text-foreground transition-colors">
                  <Mail size={16} strokeWidth={1.5} />
                  <span className="text-sm">hello@lirkprint.com</span>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noreferrer" className="flex items-center gap-3 text-foreground/60 hover:text-foreground transition-colors">
                  <Instagram size={16} strokeWidth={1.5} />
                  <span className="text-sm">@lirkprint</span>
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="p-6 bg-surface border border-border rounded-sm"
            >
              <h3 className="text-sm font-heading font-medium text-foreground mb-3">
                {t("contact.custom_order")}
              </h3>
              <p className="text-xs text-foreground/50 leading-relaxed">
                {lang === "de"
                  ? "Du hast eine besondere Vorstellung? Ich fertige individuelle 3D-Druckobjekte nach deinen Wünschen an."
                  : "Have a special vision? I create custom 3D printed objects according to your wishes."}
              </p>
            </motion.div>
          </div>
        </div>
        <div className="pb-24" />
      </div>
    </div>
  );
}
